import { ACTIONS, PHASES } from './constants';
import { countHandValue, findSubsetChoices, getTopCard, isBuildOpen, playerHasBuild, sameRoyal } from './utils';
import { isNumeric, isRoyal, numericValue } from './cards';
import type { Action, Card, GameState, ValidationResult } from './types';

export interface MoveTargets {
  looseCardIds?: string[];
  buildPileIds?: string[];
  stayPileIds?: string[];
  opponentTop?: boolean;
  opponentTeamId?: string;
  anchorLooseCardId?: string;
}

export type MoveOption = Action & {
  cardId?: string;
  label?: string;
  requiresTarget?: boolean;
  targets?: MoveTargets;
};

export interface ContextualSelection {
  looseCardIds: string[];
  buildPileIds: string[];
  stayPileIds: string[];
  includeOpponentTop?: boolean;
  opponentTeamId?: string;
}

function suitToSymbol(suit: string): string {
  if (suit === 'S') return '♠';
  if (suit === 'H') return '♥';
  if (suit === 'D') return '♦';
  if (suit === 'C') return '♣';
  return suit;
}

function fmtCardCompact(card: { rank: string; suit: string }): string {
  return `${card.rank}${suitToSymbol(card.suit)}`;
}

function countTeamHandValue(state: GameState, playerId: string, value: number, handAfterPlay?: GameState['hands'][string]): number {
  const player = state.players.find((entry) => entry.id === playerId);
  if (!player) return 0;
  const teamPlayerIds = state.players.filter((entry) => entry.teamId === player.teamId).map((entry) => entry.id);
  return teamPlayerIds.reduce((sum, pid) => {
    const cards = pid === playerId && handAfterPlay ? handAfterPlay : state.hands[pid] || [];
    return sum + countHandValue(cards, value);
  }, 0);
}

function getPlayedCardId(move: MoveOption): string | null {
  if (move.type === ACTIONS.DROP && move.cardId) return move.cardId;
  if (move.cardId) return move.cardId;
  if ('cardIdFromHand' in move) return move.cardIdFromHand;
  if ('playedCardIdFromHand' in move) return move.playedCardIdFromHand;
  return null;
}

function isCommittedResolutionMove(
  move: MoveOption,
  playedValue: number,
  ownedPileIdsWithValue: Set<string>
): boolean {
  if (move.type === ACTIONS.TAKE_PILE) {
    return true;
  }
  if (move.type === ACTIONS.TAKE_PILE_ADD_TO_BUILD || move.type === ACTIONS.TAKE_PILE_ADD_TO_STAY) {
    return true;
  }
  if (move.type === ACTIONS.REINFORCE_BUILD) {
    return ownedPileIdsWithValue.has(move.pileId);
  }
  if (move.type === ACTIONS.REINFORCE_STAY) {
    return ownedPileIdsWithValue.has(move.pileId);
  }
  return false;
}

function enforceCommittedValueRule(state: GameState, playerId: string, moves: MoveOption[]): MoveOption[] {
  const ownedBuilds = state.builds.filter((build) => build.ownerPlayerId === playerId);
  const ownedStays = state.stays.filter((stay) => stay.ownerPlayerId === playerId);
  const committedValues = new Set<number>([...ownedBuilds.map((build) => build.value), ...ownedStays.map((stay) => stay.value)]);
  if (committedValues.size === 0) return moves;

  const handById = new Map((state.hands[playerId] || []).map((card) => [card.id, card] as const));
  const ownedPileIdsByValue = new Map<number, Set<string>>();
  ownedBuilds.forEach((build) => {
    if (!ownedPileIdsByValue.has(build.value)) ownedPileIdsByValue.set(build.value, new Set());
    ownedPileIdsByValue.get(build.value)!.add(build.id);
  });
  ownedStays.forEach((stay) => {
    if (!ownedPileIdsByValue.has(stay.value)) ownedPileIdsByValue.set(stay.value, new Set());
    ownedPileIdsByValue.get(stay.value)!.add(stay.id);
  });

  return moves.filter((move) => {
    const playedCardId = getPlayedCardId(move);
    if (!playedCardId) return true;
    const playedCard = handById.get(playedCardId);
    if (!playedCard || !isNumeric(playedCard)) return true;
    const playedValue = numericValue(playedCard);
    if (!committedValues.has(playedValue)) return true;
    const ownedPileIdsWithValue = ownedPileIdsByValue.get(playedValue) || new Set<string>();
    return isCommittedResolutionMove(move, playedValue, ownedPileIdsWithValue);
  });
}

function isPendingRoyalAllowedMove(move: MoveOption): boolean {
  return (
    move.type === ACTIONS.PLACE_PENDING_ROYALS ||
    move.type === ACTIONS.TAKE_SINGLE ||
    move.type === ACTIONS.TAKE_SUBSET ||
    move.type === ACTIONS.TAKE_PILE ||
    move.type === ACTIONS.ADD_TO_BUILD ||
    move.type === ACTIONS.ADD_TO_STAY ||
    move.type === ACTIONS.REINFORCE_BUILD ||
    move.type === ACTIONS.REINFORCE_STAY
  );
}

function enforcePendingRoyalRule(state: GameState, playerId: string, moves: MoveOption[]): MoveOption[] {
  const pendingCount = state.royalPending[playerId]?.length || 0;
  if (pendingCount <= 0) return moves;
  return moves.filter(isPendingRoyalAllowedMove);
}

function isActionAllowedWhilePendingRoyal(action: Action): boolean {
  return (
    action.type === ACTIONS.PLACE_PENDING_ROYALS ||
    action.type === ACTIONS.TAKE_SINGLE ||
    action.type === ACTIONS.TAKE_SUBSET ||
    action.type === ACTIONS.TAKE_PILE ||
    action.type === ACTIONS.ADD_TO_BUILD ||
    action.type === ACTIONS.ADD_TO_STAY ||
    action.type === ACTIONS.REINFORCE_BUILD ||
    action.type === ACTIONS.REINFORCE_STAY
  );
}

export function getLegalMoves(state: GameState, playerId: string): MoveOption[] {
  if (state.phase !== PHASES.PLAYING) return [];
  if (state.currentPlayerId !== playerId) return [];
  if (state.turnContext.pendingAbsorption?.playerId === playerId) return [];

  const hand = state.hands[playerId] || [];
  const player = state.players.find((entry) => entry.id === playerId);
  const teamId = player?.teamId;
  const buildValuesOnTable = new Set<number>(state.builds.map((build) => build.value));
  const stayValuesOnTable = new Set<number>(state.stays.map((stay) => stay.value));
  const teamStayValues = new Set<number>(
    state.stays
      .filter((stay) => {
        const owner = state.players.find((p) => p.id === stay.ownerPlayerId);
        return owner?.teamId === teamId;
      })
      .map((stay) => stay.value)
  );
  const teamBuildValues = new Set<number>(
    state.builds
      .filter((build) => {
        const owner = state.players.find((p) => p.id === build.ownerPlayerId);
        return owner?.teamId === teamId;
      })
      .map((build) => build.value)
  );
  const pendingCount = state.royalPending[playerId]?.length || 0;
  const ownsBuild = state.builds.some((build) => build.ownerPlayerId === playerId);
  const ownsStay = state.stays.some((stay) => stay.ownerPlayerId === playerId);
  const canDrop = !ownsBuild && !ownsStay;

  const legal: MoveOption[] = [];
  if (pendingCount > 0) {
    legal.push({
      type: ACTIONS.PLACE_PENDING_ROYALS,
      playerId,
      label: 'Place pending royals',
      requiresTarget: false,
    });
  }

  hand.forEach((card) => {
    if (canDrop) {
      legal.push({ type: ACTIONS.DROP, playerId, cardId: card.id, label: 'Drop', requiresTarget: false });
    }

    state.tableLoose.forEach((tableCard) => {
      if (card.rank === tableCard.rank) {
        legal.push({
          type: ACTIONS.TAKE_SINGLE,
          playerId,
          cardId: card.id,
          cardIdFromHand: card.id,
          target: { type: 'looseCard', cardIdOnTable: tableCard.id },
          requiresTarget: true,
          targets: { looseCardIds: [tableCard.id] },
          label: `Take single (${fmtCardCompact(tableCard)})`,
        });

        if (isNumeric(card) && countHandValue(hand, numericValue(card)) >= 2) {
          const value = numericValue(card);
          if (teamBuildValues.has(value)) return;
          if (stayValuesOnTable.has(value) && !teamStayValues.has(value)) return;
          legal.push({
            type: ACTIONS.STAY_CREATE,
            playerId,
            cardId: card.id,
            playedCardIdFromHand: card.id,
            matchingTableCardId: tableCard.id,
            requiresTarget: true,
            targets: { looseCardIds: [tableCard.id] },
            label: `Stay (${fmtCardCompact(tableCard)})`,
          });
        }
      }
    });

    state.builds.forEach((build) => {
      if (isNumeric(card) && numericValue(card) === build.value) {
        legal.push({
          type: ACTIONS.TAKE_PILE,
          playerId,
          cardId: card.id,
          cardIdFromHand: card.id,
          target: { type: 'build', pileId: build.id },
          requiresTarget: true,
          targets: { buildPileIds: [build.id] },
          label: `Take build ${build.value}`,
        });

        if (build.ownerPlayerId === playerId && countHandValue(hand, build.value) >= 2) {
          legal.push({
            type: ACTIONS.REINFORCE_BUILD,
            playerId,
            pileId: build.id,
            cardIdFromHand: card.id,
            cardId: card.id,
            requiresTarget: true,
            targets: { buildPileIds: [build.id] },
            label: `Reinforce build ${build.value}`,
          });
        }
      }

      if (isNumeric(card) && build.ownerPlayerId !== playerId && isBuildOpen(build)) {
        const playedValue = numericValue(card);
        const requestedValue = build.value + playedValue;
        const handAfterPlay = hand.filter((entry) => entry.id !== card.id);
        if (requestedValue <= 10 && countTeamHandValue(state, playerId, requestedValue, handAfterPlay) >= 1) {
          if (buildValuesOnTable.has(requestedValue) && !teamBuildValues.has(requestedValue)) return;
          if (teamStayValues.has(requestedValue)) return;
          legal.push({
            type: ACTIONS.BUILD_UPDATE,
            playerId,
            pileId: build.id,
            playedCardIdFromHand: card.id,
            cardId: card.id,
            newValue: requestedValue,
            tableCardIdsUsed: [],
            requiresTarget: true,
            targets: { buildPileIds: [build.id] },
            label: `Increase build to ${requestedValue}`,
          });
        }
      }
    });

    state.stays.forEach((stay) => {
      if (isNumeric(card) && numericValue(card) === stay.value) {
        legal.push({
          type: ACTIONS.TAKE_PILE,
          playerId,
          cardId: card.id,
          cardIdFromHand: card.id,
          target: { type: 'stay', pileId: stay.id },
          requiresTarget: true,
          targets: { stayPileIds: [stay.id] },
          label: `Take stay ${stay.value}`,
        });

        if (stay.ownerPlayerId === playerId && countHandValue(hand, stay.value) >= 2) {
          legal.push({
            type: ACTIONS.REINFORCE_STAY,
            playerId,
            pileId: stay.id,
            cardIdFromHand: card.id,
            cardId: card.id,
            requiresTarget: true,
            targets: { stayPileIds: [stay.id] },
            label: `Reinforce stay ${stay.value}`,
          });
        }
      }
    });

    if (isNumeric(card) && state.tableLoose.length > 0) {
      const playedValue = numericValue(card);
      const handAfterPlay = hand.filter((entry) => entry.id !== card.id);
      const numericTable = state.tableLoose.filter(isNumeric);

      const combinations: string[][] = [];
      const buildCombos = (start: number, ids: string[], sum: number): void => {
        if (ids.length > 0) {
          const buildValue = playedValue + sum;
          if (buildValue <= 10) {
            combinations.push([...ids]);
          }
        }
        for (let i = start; i < numericTable.length; i += 1) {
          const t = numericTable[i];
          const nextSum = sum + numericValue(t);
          if (playedValue + nextSum > 10) continue;
          ids.push(t.id);
          buildCombos(i + 1, ids, nextSum);
          ids.pop();
        }
      };
      buildCombos(0, [], 0);

      const uniqueCombos = Array.from(new Set(combinations.map((ids) => [...ids].sort().join('|')))).map((key) =>
        key.split('|').filter(Boolean)
      );

      uniqueCombos.forEach((tableCardIdsUsed) => {
        const sumTable = tableCardIdsUsed.reduce((sum, id) => {
          const cardOnTable = numericTable.find((c) => c.id === id);
          return sum + (cardOnTable ? numericValue(cardOnTable) : 0);
        }, 0);
        const buildValue = playedValue + sumTable;
        if (buildValue < 1 || buildValue > 10) return;
        state.builds
          .filter((build) => build.ownerPlayerId !== playerId && isBuildOpen(build))
          .forEach((build) => {
            const requestedValue = build.value + buildValue;
            if (requestedValue > 10) return;
            if (countTeamHandValue(state, playerId, requestedValue, handAfterPlay) < 1) return;
            if (buildValuesOnTable.has(requestedValue) && !teamBuildValues.has(requestedValue)) return;
            if (teamStayValues.has(requestedValue)) return;
            legal.push({
              type: ACTIONS.BUILD_UPDATE,
              playerId,
              pileId: build.id,
              playedCardIdFromHand: card.id,
              cardId: card.id,
              newValue: requestedValue,
              tableCardIdsUsed: tableCardIdsUsed,
              requiresTarget: true,
              targets: { looseCardIds: tableCardIdsUsed, buildPileIds: [build.id] },
              label: `Increase build to ${requestedValue}`,
            });
          });

        if (playerHasBuild(state.builds, playerId)) return;
        if (countTeamHandValue(state, playerId, buildValue, handAfterPlay) < 1) return;
        if (buildValuesOnTable.has(buildValue)) return;
        if (teamStayValues.has(buildValue)) return;
        legal.push({
          type: ACTIONS.BUILD_CREATE,
          playerId,
          playedCardIdFromHand: card.id,
          cardId: card.id,
          tableCardIdsUsed,
          buildValue,
          isSolid: false,
          requiresTarget: true,
          targets: { looseCardIds: tableCardIdsUsed },
          label: `Build ${buildValue}`,
        });
      });
    }

    // Closed build from table: play value V, use 1+ loose numeric cards summing to V, keep a V in (team) hand.
    if (!ownsBuild && isNumeric(card) && state.tableLoose.length > 0) {
      const buildValue = numericValue(card);
      if (buildValue >= 1 && buildValue <= 10) {
        if (!teamStayValues.has(buildValue)) {
          if (buildValuesOnTable.has(buildValue)) return;
          const playerEntry = state.players.find((entry) => entry.id === playerId);
          const teamId = playerEntry?.teamId;
          const sameTeamBuildValue = Boolean(
            teamId &&
              state.builds.some((build) => {
                const owner = state.players.find((entry) => entry.id === build.ownerPlayerId);
                return owner?.teamId === teamId && build.value === buildValue;
              })
          );
          if (!sameTeamBuildValue) {
            const handAfterPlay = hand.filter((entry) => entry.id !== card.id);
            if (countTeamHandValue(state, playerId, buildValue, handAfterPlay) >= 1) {
              const choices = findSubsetChoices(state.tableLoose, buildValue).filter((choice) => choice.length >= 1);
              choices.forEach((choice) => {
                legal.push({
                  type: ACTIONS.BUILD_SOLID_FROM_TABLE,
                  playerId,
                  cardId: card.id,
                  playedCardIdFromHand: card.id,
                  tableCardIdsUsed: choice.map((c) => c.id),
                  requiresTarget: true,
                  targets: { looseCardIds: choice.map((c) => c.id) },
                  label: `Build closed ${buildValue} from table`,
                });
              });
            }
          }
        }
      }
    }

    Object.entries(state.collections).forEach(([targetTeamId, stack]) => {
      if (targetTeamId === teamId) return;
      const top = getTopCard(stack.cards);
      if (!top) return;

      if (isRoyal(top) && isRoyal(card)) {
        const pair = hand.find((otherCard) => otherCard.id !== card.id && sameRoyal(otherCard, card));
        if (pair && sameRoyal(top, card)) {
          legal.push({
            type: ACTIONS.REMOVE_ROYAL_COVER,
            playerId,
            cardId: card.id,
            cardIdFromHand1: card.id,
            cardIdFromHand2: pair.id,
            targetTeamId,
            requiresTarget: true,
            targets: { opponentTop: true, opponentTeamId: targetTeamId },
            label: `Remove royal cover ${targetTeamId}`,
          });
        }
      }

      if (isNumeric(top) && isNumeric(card) && numericValue(card) === numericValue(top)) {
        const valueX = numericValue(card);
        const hasTwoOrMore = countHandValue(hand, valueX) >= 2;

        if (hasTwoOrMore) {
          if (teamBuildValues.has(valueX)) return;
          if (stayValuesOnTable.has(valueX) && !teamStayValues.has(valueX)) return;
          legal.push({
            type: ACTIONS.STAY_FROM_OPP_COLLECTION,
            playerId,
            cardId: card.id,
            playedCardIdFromHand: card.id,
            targetTeamId,
            requiresTarget: true,
            targets: { opponentTop: true, opponentTeamId: targetTeamId },
            label: `Steal to stay ${valueX} from ${targetTeamId}`,
          });
        }
      }

      if (!ownsBuild && isNumeric(top) && isNumeric(card)) {
        const topValue = numericValue(top);
        const numericLoose = state.tableLoose.filter(isNumeric);
        const combos: string[][] = [[]];
        const backtrack = (start: number, ids: string[]): void => {
          for (let i = start; i < numericLoose.length; i += 1) {
            ids.push(numericLoose[i].id);
            combos.push([...ids]);
            backtrack(i + 1, ids);
            ids.pop();
          }
        };
        backtrack(0, []);

        const uniqueCombos = Array.from(new Set(combos.map((ids) => ids.slice().sort().join('|')))).map((key) =>
          key ? key.split('|') : []
        );

        uniqueCombos.forEach((tableCardIdsUsed) => {
          const tableAdded = tableCardIdsUsed.reduce((sum, cardId) => {
            const looseCard = numericLoose.find((entry) => entry.id === cardId);
            return sum + (looseCard ? numericValue(looseCard) : 0);
          }, 0);
          const v2 = topValue + numericValue(card) + tableAdded;
          const anchors = numericLoose.filter(
            (entry) => numericValue(entry) === v2 && !tableCardIdsUsed.includes(entry.id)
          );
          if (!anchors.length) return;

          anchors.forEach((anchor) => {
            const handAfterPlay = hand.filter((entry) => entry.id !== card.id);
            // Commitment rule: if you create a closed build value v2, you must still hold a v2 card
            // (in team hand) to take it out later; otherwise drop is forbidden and you can get stuck.
            if (countTeamHandValue(state, playerId, v2, handAfterPlay) < 1) return;
            if (buildValuesOnTable.has(v2)) return;
            legal.push({
              type: ACTIONS.INCREASE_OPP_TOP_TO_MATCH,
              playerId,
              cardId: card.id,
              playedCardIdFromHand: card.id,
              targetTeamId,
              tableCardIdsUsed,
              anchorLooseCardId: anchor.id,
              requiresTarget: true,
              targets: {
                opponentTop: true,
                opponentTeamId: targetTeamId,
                looseCardIds: [...tableCardIdsUsed, anchor.id],
                anchorLooseCardId: anchor.id,
              },
              label: `Increase opp top to ${v2}`,
            });
          });
        });

        // Build straight from opponent top: play a matching value card, use opponent top + table cards to total that value.
        if (isNumeric(card)) {
          const buildValue = numericValue(card);
          if (buildValue >= 1 && buildValue <= 10 && countTeamHandValue(state, playerId, buildValue) >= 2) {
            if (!teamStayValues.has(buildValue)) {
              if (buildValuesOnTable.has(buildValue)) return;
              const neededFromTable = buildValue - topValue;
              if (neededFromTable > 0) {
                const tableChoices = findSubsetChoices(numericLoose, neededFromTable).map((choice) => choice.map((c) => c.id));
                tableChoices.forEach((tableCardIdsUsed) => {
                  legal.push({
                    type: ACTIONS.BUILD_SOLID_FROM_OPP_TOP,
                    playerId,
                    cardId: card.id,
                    playedCardIdFromHand: card.id,
                    targetTeamId: targetTeamId,
                    tableCardIdsUsed,
                    requiresTarget: true,
                    targets: { looseCardIds: tableCardIdsUsed, opponentTop: true, opponentTeamId: targetTeamId },
                    label: `Build ${buildValue} from ${targetTeamId}`,
                  });
                });
              }
            }
          }
        }
      }
    });
  });

  const deduped = dedupeMoves(legal).filter((move) => {
    if ('cardIdFromHand2' in move && 'valueX' in move) {
      return Boolean(move.cardIdFromHand2);
    }
    return true;
  });
  const constrained = enforcePendingRoyalRule(state, playerId, enforceCommittedValueRule(state, playerId, deduped));

  // Rule: if a player can take with a numeric hand card, they may not drop that same card.
  // (Prevents "dropping a take" and keeps the game flowing toward collections.)
  const mustTakeFiltered = (() => {
    const drops = constrained.filter((m) => m.type === ACTIONS.DROP);
    if (drops.length === 0) return constrained;

    const takableCardIds = new Set<string>();
    constrained.forEach((m) => {
      if (m.type !== ACTIONS.TAKE_SINGLE && m.type !== ACTIONS.TAKE_SUBSET && m.type !== ACTIONS.TAKE_PILE) return;
      const anyMove = m as any;
      const cardId = (anyMove.cardIdFromHand as string) || (anyMove.playedCardIdFromHand as string) || (anyMove.cardId as string);
      if (typeof cardId === 'string' && cardId.length) {
        takableCardIds.add(cardId);
      }
    });

    if (takableCardIds.size === 0) return constrained;
    return constrained.filter((m) => m.type !== ACTIONS.DROP || !takableCardIds.has((m as any).cardId));
  })();

  return mustTakeFiltered;
}

export function getContextualMoves(
  state: GameState,
  playerId: string,
  handCardId: string,
  selectedTargets: ContextualSelection
): MoveOption[] {
  const legalMoves = getLegalMoves(state, playerId);
  const cardMoves = legalMoves.filter((move) => {
    if (move.type === ACTIONS.PLACE_PENDING_ROYALS) return true;
    if (move.cardId) return move.cardId === handCardId;
    if ('cardIdFromHand' in move) return move.cardIdFromHand === handCardId;
    if ('playedCardIdFromHand' in move) return move.playedCardIdFromHand === handCardId;
    if (move.type === ACTIONS.DROP) return move.cardId === handCardId;
    return false;
  });

  const hasSelection =
    selectedTargets.looseCardIds.length > 0 ||
    selectedTargets.buildPileIds.length > 0 ||
    selectedTargets.stayPileIds.length > 0 ||
    Boolean(selectedTargets.includeOpponentTop);

  if (!hasSelection) return cardMoves;

  const drops = cardMoves.filter((move) => move.type === ACTIONS.DROP);
  const selectedOppTop = Boolean(selectedTargets.includeOpponentTop);
  const selectedOppTeamId = selectedTargets.opponentTeamId;

  const matches = (move: MoveOption): boolean => {
    if (move.type === ACTIONS.DROP) return true;
    const t = move.targets || {};
    const loose = [...(t.looseCardIds || [])];
    const build = [...(t.buildPileIds || [])];
    const stay = [...(t.stayPileIds || [])];
    const opp = Boolean(t.opponentTop);
    const oppTeam = t.opponentTeamId;
    const looseOk = loose.every((id) => selectedTargets.looseCardIds.includes(id));
    const buildOk = build.every((id) => selectedTargets.buildPileIds.includes(id));
    const stayOk = stay.every((id) => selectedTargets.stayPileIds.includes(id));
    const oppOk = opp
      ? selectedOppTop && (!selectedOppTeamId || !oppTeam || oppTeam === selectedOppTeamId)
      : true;
    return (
      looseOk &&
      buildOk &&
      stayOk &&
      oppOk
    );
  };

  const matched = cardMoves.filter((move) => move.type !== ACTIONS.DROP && matches(move));
  const handCard = (state.hands[playerId] || []).find((card) => card.id === handCardId);

  const addToBuildMoves: MoveOption[] = [];
  if (
    handCard &&
    isNumeric(handCard) &&
    (selectedTargets.looseCardIds.length > 0 || Boolean(selectedTargets.includeOpponentTop))
  ) {
    const player = state.players.find((entry) => entry.id === playerId);
    const teamId = player?.teamId;
    const candidateBuildIds =
      selectedTargets.buildPileIds.length > 0
        ? selectedTargets.buildPileIds
        : (() => {
            if (!teamId) return [];
            const teamBuilds = state.builds.filter((build) => {
              const owner = state.players.find((entry) => entry.id === build.ownerPlayerId);
              return owner?.teamId === teamId;
            });
            return teamBuilds.length === 1 ? [teamBuilds[0].id] : [];
          })();

    const selectedLooseCards = selectedTargets.looseCardIds
      .map((id) => state.tableLoose.find((card) => card.id === id))
      .filter((card): card is NonNullable<typeof card> => Boolean(card));
    const allLooseNumeric = selectedLooseCards.length === selectedTargets.looseCardIds.length && selectedLooseCards.every(isNumeric);
    if (allLooseNumeric && candidateBuildIds.length > 0) {
      const looseSum = selectedLooseCards.reduce((sum, card) => sum + numericValue(card), 0);
      candidateBuildIds.forEach((buildId) => {
        const build = state.builds.find((entry) => entry.id === buildId);
        if (!build) return;
        const owner = state.players.find((entry) => entry.id === build.ownerPlayerId);
        const isOwnBuild = build.ownerPlayerId === playerId;
        const isPartnerBuild = Boolean(teamId && owner?.teamId && owner.teamId === teamId);
        if (!isOwnBuild && !isPartnerBuild) return;
        const handAfterPlay = (state.hands[playerId] || []).filter((entry) => entry.id !== handCard.id);
        if (countTeamHandValue(state, playerId, build.value, handAfterPlay) < 1) return;
        const baseTotal = numericValue(handCard) + looseSum;

        if (!selectedTargets.includeOpponentTop) {
          if (baseTotal !== build.value) return;
          addToBuildMoves.push({
            type: ACTIONS.ADD_TO_BUILD,
            playerId,
            pileId: build.id,
            playedCardIdFromHand: handCard.id,
            tableCardIdsUsed: selectedLooseCards.map((card) => card.id),
            cardId: handCard.id,
            label: `Add to build ${build.value} (using ${fmtCardCompact(handCard)} + ${selectedLooseCards.map(fmtCardCompact).join(' + ')})`,
            requiresTarget: true,
            targets: { buildPileIds: [build.id], looseCardIds: selectedLooseCards.map((card) => card.id) },
          });
          return;
        }

        const opponentCollections = Object.entries(state.collections)
          .filter(([collectionTeamId]) => collectionTeamId !== teamId)
          .map(([collectionTeamId, stack]) => ({ collectionTeamId, top: getTopCard(stack.cards) }))
          .filter((entry): entry is { collectionTeamId: string; top: NonNullable<ReturnType<typeof getTopCard>> } => Boolean(entry.top));

        opponentCollections.forEach(({ collectionTeamId, top }) => {
          if (!isNumeric(top)) return;
          const totalWithOpponentTop = baseTotal + numericValue(top);
          if (totalWithOpponentTop !== build.value) return;
          addToBuildMoves.push({
            type: ACTIONS.ADD_TO_BUILD,
            playerId,
            pileId: build.id,
            playedCardIdFromHand: handCard.id,
            tableCardIdsUsed: selectedLooseCards.map((card) => card.id),
            includeOpponentTop: true,
            opponentTeamId: collectionTeamId,
            cardId: handCard.id,
            label: `Add to build ${build.value} (using ${fmtCardCompact(handCard)} + ${selectedLooseCards.map(fmtCardCompact).join(' + ')} + oppTop ${fmtCardCompact(top)})`,
            requiresTarget: true,
            targets: {
              buildPileIds: [build.id],
              looseCardIds: selectedLooseCards.map((card) => card.id),
              opponentTop: true,
              opponentTeamId: collectionTeamId,
            },
          });
        });
      });
    }
  }

  const addToStayMoves: MoveOption[] = [];
  if (
    handCard &&
    isNumeric(handCard) &&
    selectedTargets.stayPileIds.length > 0 &&
    (selectedTargets.looseCardIds.length > 0 || Boolean(selectedTargets.includeOpponentTop))
  ) {
    const player = state.players.find((entry) => entry.id === playerId);
    const teamId = player?.teamId;
    const selectedLooseCards = selectedTargets.looseCardIds
      .map((id) => state.tableLoose.find((card) => card.id === id))
      .filter((card): card is NonNullable<typeof card> => Boolean(card));
    const allLooseNumeric = selectedLooseCards.every(isNumeric);
    if (allLooseNumeric && teamId) {
      const looseSum = selectedLooseCards.reduce((sum, card) => sum + numericValue(card), 0);
      selectedTargets.stayPileIds.forEach((stayId) => {
        const stay = state.stays.find((entry) => entry.id === stayId);
        if (!stay) return;
        const owner = state.players.find((entry) => entry.id === stay.ownerPlayerId);
        if (owner?.teamId !== teamId) return;
        const handAfterPlay = (state.hands[playerId] || []).filter((entry) => entry.id !== handCard.id);
        if (countHandValue(handAfterPlay, stay.value) < 1) return;

        if (!selectedTargets.includeOpponentTop) {
          if (numericValue(handCard) + looseSum !== stay.value) return;
          addToStayMoves.push({
            type: ACTIONS.ADD_TO_STAY,
            playerId,
            pileId: stay.id,
            playedCardIdFromHand: handCard.id,
            tableCardIdsUsed: selectedLooseCards.map((card) => card.id),
            cardId: handCard.id,
            label: `Add to stay ${stay.value} (using ${fmtCardCompact(handCard)} + ${selectedLooseCards.map(fmtCardCompact).join(' + ')})`,
            requiresTarget: true,
            targets: { stayPileIds: [stay.id], looseCardIds: selectedLooseCards.map((card) => card.id) },
          });
          return;
        }

        const opponentCollections = Object.entries(state.collections)
          .filter(([collectionTeamId]) => collectionTeamId !== teamId)
          .map(([collectionTeamId, stack]) => ({ collectionTeamId, top: getTopCard(stack.cards) }))
          .filter((entry): entry is { collectionTeamId: string; top: NonNullable<ReturnType<typeof getTopCard>> } => Boolean(entry.top));

        opponentCollections.forEach(({ collectionTeamId, top }) => {
          if (!isNumeric(top)) return;
          const totalWithOpponentTop = numericValue(handCard) + looseSum + numericValue(top);
          if (totalWithOpponentTop !== stay.value) return;
          addToStayMoves.push({
            type: ACTIONS.ADD_TO_STAY,
            playerId,
            pileId: stay.id,
            playedCardIdFromHand: handCard.id,
            tableCardIdsUsed: selectedLooseCards.map((card) => card.id),
            includeOpponentTop: true,
            opponentTeamId: collectionTeamId,
            cardId: handCard.id,
            label: `Add to stay ${stay.value} (using ${fmtCardCompact(handCard)} + ${selectedLooseCards.map(fmtCardCompact).join(' + ')} + oppTop ${fmtCardCompact(top)})`,
            requiresTarget: true,
            targets: {
              stayPileIds: [stay.id],
              looseCardIds: selectedLooseCards.map((card) => card.id),
              opponentTop: true,
              opponentTeamId: collectionTeamId,
            },
          });
        });
      });
    }
  }

  const reinforceAbsorbMoves: MoveOption[] = [];
  if (handCard && isNumeric(handCard) && (selectedTargets.looseCardIds.length > 0 || selectedOppTop)) {
    const player = state.players.find((entry) => entry.id === playerId);
    const teamId = player?.teamId;
    const handValue = numericValue(handCard);

    const selectedLooseCards = selectedTargets.looseCardIds
      .map((id) => state.tableLoose.find((card) => card.id === id))
      .filter((card): card is NonNullable<typeof card> => Boolean(card));
    const allLooseNumeric =
      selectedLooseCards.length === selectedTargets.looseCardIds.length && selectedLooseCards.every(isNumeric);
    if (allLooseNumeric) {
      const looseSum = selectedLooseCards.reduce((sum, card) => sum + numericValue(card), 0);
      const opponentTeamIds = selectedOppTop
        ? selectedOppTeamId
          ? [selectedOppTeamId]
          : Object.keys(state.collections).filter((collectionTeamId) => collectionTeamId !== teamId)
        : [];

      const ownBuildIds =
        selectedTargets.buildPileIds.length > 0
          ? selectedTargets.buildPileIds
          : (() => {
              const owned = state.builds.filter((b) => b.ownerPlayerId === playerId);
              return owned.length === 1 ? [owned[0].id] : [];
            })();
      ownBuildIds.forEach((buildId) => {
        const build = state.builds.find((b) => b.id === buildId);
        if (!build || build.ownerPlayerId !== playerId) return;
        if (build.value !== handValue) return;
        const handAfterPlay = (state.hands[playerId] || []).filter((entry) => entry.id !== handCard.id);
        if (countTeamHandValue(state, playerId, build.value, handAfterPlay) < 1) return;

        if (!selectedOppTop) {
          if (looseSum !== build.value) return;
          reinforceAbsorbMoves.push({
            type: ACTIONS.REINFORCE_BUILD,
            playerId,
            pileId: build.id,
            cardIdFromHand: handCard.id,
            cardId: handCard.id,
            absorb: {
              tableCardIds: selectedTargets.looseCardIds,
              opponentTeamIds: [],
            },
            label: `Reinforce build ${build.value} + absorb (${selectedLooseCards.map(fmtCardCompact).join(' + ')})`,
            requiresTarget: true,
            targets: { buildPileIds: [build.id], looseCardIds: selectedTargets.looseCardIds },
          });
          return;
        }

        opponentTeamIds.forEach((oppTeamId) => {
          const stack = state.collections[oppTeamId];
          const top = stack ? getTopCard(stack.cards) : null;
          if (!top || !isNumeric(top)) return;
          if (looseSum + numericValue(top) !== build.value) return;
          reinforceAbsorbMoves.push({
            type: ACTIONS.REINFORCE_BUILD,
            playerId,
            pileId: build.id,
            cardIdFromHand: handCard.id,
            cardId: handCard.id,
            absorb: {
              tableCardIds: selectedTargets.looseCardIds,
              opponentTeamIds: [oppTeamId],
            },
            label: `Reinforce build ${build.value} + absorb (${selectedLooseCards.map(fmtCardCompact).join(' + ')} + oppTop ${fmtCardCompact(top)})`,
            requiresTarget: true,
            targets: {
              buildPileIds: [build.id],
              looseCardIds: selectedTargets.looseCardIds,
              opponentTop: true,
              opponentTeamId: oppTeamId,
            },
          });
        });
      });

      const ownStayIds =
        selectedTargets.stayPileIds.length > 0
          ? selectedTargets.stayPileIds
          : (() => {
              const owned = state.stays.filter((s) => s.ownerPlayerId === playerId);
              return owned.length === 1 ? [owned[0].id] : [];
            })();
      ownStayIds.forEach((stayId) => {
        const stay = state.stays.find((s) => s.id === stayId);
        if (!stay || stay.ownerPlayerId !== playerId) return;
        if (stay.value !== handValue) return;
        const handAfterPlay = (state.hands[playerId] || []).filter((entry) => entry.id !== handCard.id);
        if (countHandValue(handAfterPlay, stay.value) < 1) return;

        if (!selectedOppTop) {
          if (looseSum !== stay.value) return;
          reinforceAbsorbMoves.push({
            type: ACTIONS.REINFORCE_STAY,
            playerId,
            pileId: stay.id,
            cardIdFromHand: handCard.id,
            cardId: handCard.id,
            absorb: {
              tableCardIds: selectedTargets.looseCardIds,
              opponentTeamIds: [],
            },
            label: `Reinforce stay ${stay.value} + absorb (${selectedLooseCards.map(fmtCardCompact).join(' + ')})`,
            requiresTarget: true,
            targets: { stayPileIds: [stay.id], looseCardIds: selectedTargets.looseCardIds },
          });
          return;
        }

        opponentTeamIds.forEach((oppTeamId) => {
          const stack = state.collections[oppTeamId];
          const top = stack ? getTopCard(stack.cards) : null;
          if (!top || !isNumeric(top)) return;
          if (looseSum + numericValue(top) !== stay.value) return;
          reinforceAbsorbMoves.push({
            type: ACTIONS.REINFORCE_STAY,
            playerId,
            pileId: stay.id,
            cardIdFromHand: handCard.id,
            cardId: handCard.id,
            absorb: {
              tableCardIds: selectedTargets.looseCardIds,
              opponentTeamIds: [oppTeamId],
            },
            label: `Reinforce stay ${stay.value} + absorb (${selectedLooseCards.map(fmtCardCompact).join(' + ')} + oppTop ${fmtCardCompact(top)})`,
            requiresTarget: true,
            targets: {
              stayPileIds: [stay.id],
              looseCardIds: selectedTargets.looseCardIds,
              opponentTop: true,
              opponentTeamId: oppTeamId,
            },
          });
        });
      });
    }
  }

  // Subset-sum take: selected loose-card subsets can be taken by numeric hand card.
  const subsetTakeMoves: MoveOption[] = [];
  if (handCard && isNumeric(handCard) && selectedTargets.looseCardIds.length > 0) {
    const selectedLooseCards = selectedTargets.looseCardIds
      .map((id) => state.tableLoose.find((card) => card.id === id))
      .filter((card): card is NonNullable<typeof card> => Boolean(card))
      .filter(isNumeric);

    const targetValue = numericValue(handCard);
    const combos: string[][] = [];
    const backtrack = (start: number, ids: string[], sum: number): void => {
      if (sum === targetValue && ids.length > 0) {
        combos.push([...ids]);
        return;
      }
      if (sum >= targetValue) return;
      for (let i = start; i < selectedLooseCards.length; i += 1) {
        const c = selectedLooseCards[i];
        ids.push(c.id);
        backtrack(i + 1, ids, sum + numericValue(c));
        ids.pop();
      }
    };
    backtrack(0, [], 0);

    const unique = Array.from(new Set(combos.map((ids) => [...ids].sort().join('|')))).map((k) => k.split('|'));
    const selectedById = new Map(selectedLooseCards.map((card) => [card.id, card] as const));
    unique.forEach((cardIds) => {
      if (cardIds.length < 2) return;
      subsetTakeMoves.push({
        type: ACTIONS.TAKE_SUBSET,
        playerId,
        cardId: handCardId,
        cardIdFromHand: handCardId,
        target: { type: 'looseSubset', cardIdsOnTable: cardIds },
        label: `Take subset (${cardIds.map((id) => selectedById.get(id)).filter(Boolean).map(fmtCardCompact).join(' + ')})`,
        requiresTarget: true,
        targets: { looseCardIds: cardIds },
      });
    });
  }

  const takeAndSupportMoves: MoveOption[] = [];
  if (handCard) {
    const player = state.players.find((entry) => entry.id === playerId);
    const teamId = player?.teamId;
    const includeOpponentTop = Boolean(selectedTargets.includeOpponentTop);
    const opponentTeamId = selectedTargets.opponentTeamId;
    const handAfterPlay = (state.hands[playerId] || []).filter((entry) => entry.id !== handCard.id);

    const selectedLooseCards = selectedTargets.looseCardIds
      .map((id) => state.tableLoose.find((card) => card.id === id))
      .filter((card): card is NonNullable<typeof card> => Boolean(card));
    const selectedLooseById = new Map(selectedLooseCards.map((card) => [card.id, card] as const));

    const opponentTopCard = includeOpponentTop && teamId && opponentTeamId
      ? (() => {
          if (opponentTeamId === teamId) return null;
          const stack = state.collections[opponentTeamId];
          const top = stack ? getTopCard(stack.cards) : null;
          return top && isNumeric(top) ? top : null;
        })()
      : null;

    const allSelectedLooseKnown = selectedLooseCards.length === selectedTargets.looseCardIds.length;

    type Dest =
      | { kind: 'build'; pileId: string; value: number }
      | { kind: 'stay'; pileId: string; value: number };

    const selectedDestinations: Dest[] = [];
    if (teamId) {
      selectedTargets.buildPileIds.forEach((pileId) => {
        const pile = state.builds.find((entry) => entry.id === pileId);
        if (!pile) return;
        const owner = state.players.find((p) => p.id === pile.ownerPlayerId);
        if (owner?.teamId !== teamId) return;
        selectedDestinations.push({ kind: 'build', pileId, value: pile.value });
      });
      selectedTargets.stayPileIds.forEach((pileId) => {
        const pile = state.stays.find((entry) => entry.id === pileId);
        if (!pile) return;
        const owner = state.players.find((p) => p.id === pile.ownerPlayerId);
        if (owner?.teamId !== teamId) return;
        selectedDestinations.push({ kind: 'stay', pileId, value: pile.value });
      });
    }

    const canSupportDestination = (dest: Dest): boolean => {
      if (dest.kind === 'build') {
        return teamId ? countTeamHandValue(state, playerId, dest.value, handAfterPlay) >= 1 : false;
      }
      return countHandValue(handAfterPlay, dest.value) >= 1;
    };

    const trySupportSingleTake = (takeLooseId: string, dest: Dest): void => {
      if (!allSelectedLooseKnown) return;
      const remainingLooseIds = selectedTargets.looseCardIds.filter((id) => id !== takeLooseId);
      const remainingLooseCards = remainingLooseIds.map((id) => selectedLooseById.get(id)).filter((c): c is Card => Boolean(c));
      if (remainingLooseCards.length !== remainingLooseIds.length) return;
      if (remainingLooseCards.some((card) => !isNumeric(card))) return;
      const looseSum = remainingLooseCards.reduce((sum, card) => sum + numericValue(card), 0);

      if (includeOpponentTop) {
        if (!opponentTeamId || !opponentTopCard) return;
        if (looseSum + numericValue(opponentTopCard) !== dest.value) return;
      } else {
        if (looseSum !== dest.value) return;
      }
      if (!canSupportDestination(dest)) return;

      const supportLabelParts: string[] = [];
      if (remainingLooseCards.length) supportLabelParts.push(remainingLooseCards.map(fmtCardCompact).join(' + '));
      if (includeOpponentTop && opponentTopCard) supportLabelParts.push(`oppTop ${fmtCardCompact(opponentTopCard)}`);
      const supportLabel = supportLabelParts.join(' + ') || '(no support cards)';

      if (dest.kind === 'build') {
        takeAndSupportMoves.push({
          type: ACTIONS.TAKE_SINGLE_ADD_TO_BUILD,
          playerId,
          cardId: handCard.id,
          cardIdFromHand: handCard.id,
          takeCardIdOnTable: takeLooseId,
          supportPileId: dest.pileId,
          supportTableCardIdsUsed: remainingLooseIds,
          includeOpponentTop: includeOpponentTop || undefined,
          opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
          label: `Take + support build ${dest.value} (${supportLabel})`,
          requiresTarget: true,
          targets: {
            looseCardIds: selectedTargets.looseCardIds,
            buildPileIds: selectedTargets.buildPileIds,
            stayPileIds: selectedTargets.stayPileIds,
            opponentTop: includeOpponentTop || undefined,
            opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
          },
        });
      } else {
        takeAndSupportMoves.push({
          type: ACTIONS.TAKE_SINGLE_ADD_TO_STAY,
          playerId,
          cardId: handCard.id,
          cardIdFromHand: handCard.id,
          takeCardIdOnTable: takeLooseId,
          supportPileId: dest.pileId,
          supportTableCardIdsUsed: remainingLooseIds,
          includeOpponentTop: includeOpponentTop || undefined,
          opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
          label: `Take + support stay ${dest.value} (${supportLabel})`,
          requiresTarget: true,
          targets: {
            looseCardIds: selectedTargets.looseCardIds,
            buildPileIds: selectedTargets.buildPileIds,
            stayPileIds: selectedTargets.stayPileIds,
            opponentTop: includeOpponentTop || undefined,
            opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
          },
        });
      }
    };

    if (selectedTargets.looseCardIds.length > 0 && selectedDestinations.length > 0) {
      selectedLooseCards
        .filter((card) => card.rank === handCard.rank)
        .forEach((tableCard) => {
          selectedDestinations.forEach((dest) => trySupportSingleTake(tableCard.id, dest));
        });
    }

    if (isNumeric(handCard) && selectedTargets.looseCardIds.length > 0 && selectedDestinations.length > 0) {
      const numericSelected = selectedLooseCards.filter(isNumeric);
      if (numericSelected.length === selectedLooseCards.length) {
        const targetValue = numericValue(handCard);
        const subsetChoices = findSubsetChoices(numericSelected, targetValue)
          .map((subset) => subset.map((card) => card.id))
          .filter((ids) => ids.length > 1);
        const unique = Array.from(new Set(subsetChoices.map((ids) => [...ids].sort().join('|')))).map((k) => k.split('|'));
        unique.forEach((takeIds) => {
          selectedDestinations.forEach((dest) => {
            if (!allSelectedLooseKnown) return;
            const remainingLooseIds = selectedTargets.looseCardIds.filter((id) => !takeIds.includes(id));
            const remainingLooseCards = remainingLooseIds.map((id) => selectedLooseById.get(id)).filter((c): c is Card => Boolean(c));
            if (remainingLooseCards.length !== remainingLooseIds.length) return;
            if (remainingLooseCards.some((card) => !isNumeric(card))) return;
            const looseSum = remainingLooseCards.reduce((sum, card) => sum + numericValue(card), 0);

            if (includeOpponentTop) {
              if (!opponentTeamId || !opponentTopCard) return;
              if (looseSum + numericValue(opponentTopCard) !== dest.value) return;
            } else {
              if (looseSum !== dest.value) return;
            }
            if (!canSupportDestination(dest)) return;

            const supportLabelParts: string[] = [];
            if (remainingLooseCards.length) supportLabelParts.push(remainingLooseCards.map(fmtCardCompact).join(' + '));
            if (includeOpponentTop && opponentTopCard) supportLabelParts.push(`oppTop ${fmtCardCompact(opponentTopCard)}`);
            const supportLabel = supportLabelParts.join(' + ') || '(no support cards)';

            if (dest.kind === 'build') {
              takeAndSupportMoves.push({
                type: ACTIONS.TAKE_SUBSET_ADD_TO_BUILD,
                playerId,
                cardId: handCard.id,
                cardIdFromHand: handCard.id,
                takeCardIdsOnTable: takeIds,
                supportPileId: dest.pileId,
                supportTableCardIdsUsed: remainingLooseIds,
                includeOpponentTop: includeOpponentTop || undefined,
                opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
                label: `Take subset + support build ${dest.value} (${supportLabel})`,
                requiresTarget: true,
                targets: {
                  looseCardIds: selectedTargets.looseCardIds,
                  buildPileIds: selectedTargets.buildPileIds,
                  stayPileIds: selectedTargets.stayPileIds,
                  opponentTop: includeOpponentTop || undefined,
                  opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
                },
              });
            } else {
              takeAndSupportMoves.push({
                type: ACTIONS.TAKE_SUBSET_ADD_TO_STAY,
                playerId,
                cardId: handCard.id,
                cardIdFromHand: handCard.id,
                takeCardIdsOnTable: takeIds,
                supportPileId: dest.pileId,
                supportTableCardIdsUsed: remainingLooseIds,
                includeOpponentTop: includeOpponentTop || undefined,
                opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
                label: `Take subset + support stay ${dest.value} (${supportLabel})`,
                requiresTarget: true,
                targets: {
                  looseCardIds: selectedTargets.looseCardIds,
                  buildPileIds: selectedTargets.buildPileIds,
                  stayPileIds: selectedTargets.stayPileIds,
                  opponentTop: includeOpponentTop || undefined,
                  opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
                },
              });
            }
          });
        });
      }
    }

    if (isNumeric(handCard) && selectedDestinations.length > 0) {
      const handValue = numericValue(handCard);
      const takePileCandidates: Array<{ kind: 'build' | 'stay'; pileId: string; value: number }> = [];
      selectedTargets.buildPileIds.forEach((pileId) => {
        const pile = state.builds.find((entry) => entry.id === pileId);
        if (pile && pile.value === handValue) takePileCandidates.push({ kind: 'build', pileId, value: pile.value });
      });
      selectedTargets.stayPileIds.forEach((pileId) => {
        const pile = state.stays.find((entry) => entry.id === pileId);
        if (pile && pile.value === handValue) takePileCandidates.push({ kind: 'stay', pileId, value: pile.value });
      });

      takePileCandidates.forEach((takePile) => {
        selectedDestinations
          .filter((dest) => dest.pileId !== takePile.pileId)
          .forEach((dest) => {
            if (!allSelectedLooseKnown) return;
            const supportLooseIds = selectedTargets.looseCardIds;
            const supportLooseCards = supportLooseIds.map((id) => selectedLooseById.get(id)).filter((c): c is Card => Boolean(c));
            if (supportLooseCards.length !== supportLooseIds.length) return;
            if (supportLooseCards.some((card) => !isNumeric(card))) return;
            const looseSum = supportLooseCards.reduce((sum, card) => sum + numericValue(card), 0);

            if (includeOpponentTop) {
              if (!opponentTeamId || !opponentTopCard) return;
              if (looseSum + numericValue(opponentTopCard) !== dest.value) return;
            } else {
              if (looseSum !== dest.value) return;
            }
            if (!canSupportDestination(dest)) return;

            const supportLabelParts: string[] = [];
            if (supportLooseCards.length) supportLabelParts.push(supportLooseCards.map(fmtCardCompact).join(' + '));
            if (includeOpponentTop && opponentTopCard) supportLabelParts.push(`oppTop ${fmtCardCompact(opponentTopCard)}`);
            const supportLabel = supportLabelParts.join(' + ') || '(no support cards)';

            if (dest.kind === 'build') {
              takeAndSupportMoves.push({
                type: ACTIONS.TAKE_PILE_ADD_TO_BUILD,
                playerId,
                cardId: handCard.id,
                cardIdFromHand: handCard.id,
                takeTarget: { type: takePile.kind, pileId: takePile.pileId },
                supportPileId: dest.pileId,
                supportTableCardIdsUsed: supportLooseIds,
                includeOpponentTop: includeOpponentTop || undefined,
                opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
                label: `Take pile + support build ${dest.value} (${supportLabel})`,
                requiresTarget: true,
                targets: {
                  looseCardIds: selectedTargets.looseCardIds,
                  buildPileIds: selectedTargets.buildPileIds,
                  stayPileIds: selectedTargets.stayPileIds,
                  opponentTop: includeOpponentTop || undefined,
                  opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
                },
              });
            } else {
              takeAndSupportMoves.push({
                type: ACTIONS.TAKE_PILE_ADD_TO_STAY,
                playerId,
                cardId: handCard.id,
                cardIdFromHand: handCard.id,
                takeTarget: { type: takePile.kind, pileId: takePile.pileId },
                supportPileId: dest.pileId,
                supportTableCardIdsUsed: supportLooseIds,
                includeOpponentTop: includeOpponentTop || undefined,
                opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
                label: `Take pile + support stay ${dest.value} (${supportLabel})`,
                requiresTarget: true,
                targets: {
                  looseCardIds: selectedTargets.looseCardIds,
                  buildPileIds: selectedTargets.buildPileIds,
                  stayPileIds: selectedTargets.stayPileIds,
                  opponentTop: includeOpponentTop || undefined,
                  opponentTeamId: includeOpponentTop ? opponentTeamId : undefined,
                },
              });
            }
          });
      });
    }
  }

  const applyAbsorbConstraints = (move: MoveOption): MoveOption => {
    if (
      move.type !== ACTIONS.TAKE_SINGLE &&
      move.type !== ACTIONS.TAKE_SUBSET &&
      move.type !== ACTIONS.TAKE_PILE
    ) {
      return move;
    }
    if (selectedTargets.looseCardIds.length <= 0 && !selectedOppTop) return move;
    const opponentTeamIds = selectedOppTop && selectedOppTeamId ? [selectedOppTeamId] : [];
    return {
      ...move,
      absorb: {
        tableCardIds: selectedTargets.looseCardIds,
        opponentTeamIds,
      },
    };
  };

  return enforceCommittedValueRule(
    state,
    playerId,
    enforcePendingRoyalRule(
      state,
      playerId,
      dedupeMoves(
        [...drops, ...matched, ...addToBuildMoves, ...addToStayMoves, ...reinforceAbsorbMoves, ...subsetTakeMoves, ...takeAndSupportMoves].map(
          applyAbsorbConstraints
        )
      )
    )
  );
}

function dedupeMoves(moves: MoveOption[]): MoveOption[] {
  const map = new Map<string, MoveOption>();
  moves.forEach((move) => {
    const key = JSON.stringify(move);
    if (!map.has(key)) {
      map.set(key, move);
    }
  });
  return Array.from(map.values());
}

export function validateMove(state: GameState, action: Action): ValidationResult {
  if (!action || !action.type) {
    return { valid: false, reason: 'Action type is required.' };
  }

  if (action.type === ACTIONS.NEW_GAME) {
    return { valid: true };
  }

  if (action.type === ACTIONS.DEAL_INITIAL || action.type === ACTIONS.START_ROUND) {
    if (state.phase !== PHASES.BETWEEN_ROUNDS) {
      return { valid: false, reason: 'Can only deal/start when phase is betweenRounds.' };
    }
    return { valid: true };
  }

  if (action.type === ACTIONS.TAKE_ROYAL_FROM_COLLECTION) {
    return { valid: false, reason: 'Taking royals directly from opponent collection is disabled.' };
  }

  if (state.phase !== PHASES.PLAYING) {
    return { valid: false, reason: 'Turn actions are only allowed in playing phase.' };
  }

  if (!('playerId' in action)) {
    return { valid: false, reason: 'Action requires playerId.' };
  }

  if (state.currentPlayerId !== action.playerId) {
    return { valid: false, reason: 'Not this player turn.' };
  }

  const mustPlacePendingRoyal = state.royalPending[action.playerId]?.length > 0;
  if (mustPlacePendingRoyal && !isActionAllowedWhilePendingRoyal(action)) {
    return { valid: false, reason: 'Pending royals: only place, take, or support team build actions are allowed.' };
  }

  if (state.turnContext.pendingAbsorption && action.type !== ACTIONS.ABSORB_CHOOSE && action.type !== ACTIONS.ABSORB_SKIP) {
    return { valid: false, reason: 'Resolve pending absorption choices first.' };
  }

  return { valid: true };
}
